'use client'

import React, { useRef } from 'react'
import { useBaseBetslip, useChain, useGameState } from '@azuro-org/sdk'
import { ConditionState, GameState } from '@azuro-org/toolkit'
import cx from 'classnames'
import { Message } from '@locmod/intl'
import { constants } from 'helpers'

import { formatToFixed } from 'helpers/formatters'
import useOddsChange from 'src/hooks/useOddsChange'
import { Input } from 'components/inputs'
import { Warning } from 'components/feedback'
import { Icon, LiveDot, type IconName } from 'components/ui'
import OddsValue from 'compositions/OddsValue/OddsValue'

import messages from './messages'
import { useTicket } from '@/contexts/TicketContext'
import { getPositionTextV2, getTitleText } from '@/Overtime/utils/marketsV2'
import { formatMarketOdds } from '@/Overtime/utils/markets'
import { OddsType } from '@/Overtime/enums/markets'

type ItemProps = {
  item: any
  // batchBetAmount: string
  state: any
  odds: number
  isStatesFetching: boolean
  isOddsFetching: boolean
  // isBatch: boolean
  // onBatchAmountChange: (value: string) => void
}

const OvertimeCard: React.FC<ItemProps> = props => {
  const { item, odds, state, isOddsFetching, isStatesFetching } = props

  const { removeFromTicket } = useTicket()

  const { market, position } = item

  const {
    marketName,
    displayPosition,
    game,
    leagueName,
    homeTeam,
    awayTeam,
    odd,
    live,
  } = market

  const positionText = getPositionTextV2(market, displayPosition, true)
  const titleText = getTitleText(market)

  // const {
  //   gameId,
  //   title,
  //   sport: { slug: sportSlug },
  //   country: { name: countryName },
  //   league: { name: leagueName },
  // } = game

  // const { appChain, betToken } = useChain()
  // const { removeItem } = useBaseBetslip()
  // const { data: gameState } = useGameState({
  //   gameId,
  //   initialState: game.state,
  // })
  const nodeRef = useRef<HTMLDivElement>(null)
  useOddsChange({ odds, nodeRef })
  // const oddsRef = useRef(odds)

  // if (!isOddsFetching) {
  //   oddsRef.current = odds
  // }

  const isDisabled = !isStatesFetching && state !== 'active'
  // const isLive = gameState === GameState.Live
  const isUnique = false // sportSlug === 'unique'

  const isPlayerNameEmpty =
    item.playerProps && item.playerProps.playerName === ''

  const bottomBoxClassName = cx(
    'px-4 py-2 mt-px',
    'group-[.increased]/card:bg-betslip-item-bg-inc',
    'group-[.decreased]/card:bg-betslip-item-bg-dec',
    {
      'bg-betslip-item-bg': !isDisabled,
      'bg-accent-grey-850': isDisabled,
    }
  )
  const oddsClassName = cx(
    'h-5 flex items-center px-[0.375rem] bg-brand-900 border',
    'border-brand-800 rounded-sm text-accent-grey-90 text-caption-12',
    'group-[.increased]/card:bg-accent-green-1000 group-[.increased]/card:border-accent-green-700 group-[.increased]/card:text-accent-green-500',
    'group-[.decreased]/card:bg-accent-red-1000 group-[.decreased]/card:border-accent-red-700 group-[.decreased]/card:text-accent-red-500'
  )

  return (
    <div ref={nodeRef} className="group/card rounded-md overflow-hidden">
      <div className="bg-accent-grey-850 py-2 px-4">
        <div className="flex items-center justify-between">
          <div
            className={cx('flex items-center text-caption-12 max-w-[90%]', {
              'text-accent-grey-500': isDisabled,
              'text-accent-grey-200': !isDisabled,
            })}
          >
            <Icon
              className="size-4 mr-1 flex-none"
              name={'sport/football' as IconName} // TODO: add sport slug
            />
            <>
              <div className="whitespace-nowrap">Country</div>
              <div className="size-1 rounded-full bg-accent-grey-700 mx-1" />
              <div className="text-ellipsis whitespace-nowrap overflow-hidden">
                {leagueName}
              </div>
            </>
          </div>
          <button
            className="text-accent-grey-200 hover:text-accent-grey-30 transition ml-2 p-1"
            onClick={() => removeFromTicket(item)}
          >
            <Icon className="size-3" name="interface/close" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center">
            <div
              className={cx('text-caption-13 font-semibold mr-1', {
                'text-accent-grey-500': isDisabled,
              })}
            >
              {homeTeam} - {awayTeam}
            </div>
            {live && <LiveDot />}
          </div>
          {isDisabled && (
            <Icon
              className="text-accent-grey-200 size-4"
              name="interface/lock"
            />
          )}
        </div>
      </div>
      <div className={bottomBoxClassName}>
        {isDisabled ? (
          <Warning text={messages.locked} />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-caption-12">
                {!isUnique && (
                  <span className="text-accent-grey-200 mr-1">
                    {titleText}:
                  </span>
                )}
                {isPlayerNameEmpty ? (
                  <span>{positionText}</span>
                ) : (
                  <span>{item.playerProps.playerName}</span>
                )}
              </div>
              {isOddsFetching ? (
                <Icon className="size-5" name="interface/spinner" />
              ) : // Show '--' if odds is 0, otherwise show OddsValue
              odds === 0 ? (
                <span className={oddsClassName}>--</span>
              ) : (
                <OddsValue
                  className={oddsClassName}
                  odds={formatMarketOdds(OddsType.DECIMAL, odds)}
                />
              )}
            </div>

            {/* {isBatch && (
              <div className="flex items-center mt-2">
                <Input
                  className="bg-accent-grey-800"
                  type="number"
                  placeholder="0.00"
                  leftNode={
                    <Icon
                      className="size-4 mr-2"
                      name={constants.currencyIcons[appChain.id]}
                    />
                  }
                  onChange={onBatchAmountChange}
                />
                <div className="text-right ml-3">
                  <Message
                    className="text-caption-12 text-accent-grey-200 w-max"
                    value={messages.possibleWin}
                    tag="p"
                  />
                  <div className="text-caption-13 text-brand-light-500 font-semibold mt-0.5 w-max text-right ml-auto">
                    {formatToFixed(oddsRef.current * +batchBetAmount, 2)}{' '}
                    {betToken.symbol}
                  </div>
                </div>
              </div>
            )} */}
          </>
        )}
      </div>
    </div>
  )
}

export default OvertimeCard
